"use client";
import LoginModal from "@components/molecules/LoginModal";
import React, { useState, useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import styles from "./HeaderClientWrapper.module.scss";
import { BuyingIntentData } from '@services/globalIntentService';
import dynamic from "next/dynamic";
import { PageType } from "@constants/pageTypes";
import { useIsMobile } from "@hooks/use-mobile";
import { shouldHideHeader } from "@utils/headerVisibility";

const GlobalIntentModal = dynamic(
  () => import("@components/molecules/GlobalIntentModal"),
  {
    ssr: false,
    loading: () => null,
  }
);

type HeaderClientWrapperProps = {
  children: React.ReactNode;
  isAuthenticated: boolean;
  buyingIntentData: BuyingIntentData | null;
  cityName: string;
  initialIsMobile: boolean;
  initialHideHeader: boolean;
};

function HeaderClientWrapper({ 
  children, 
  isAuthenticated, 
  buyingIntentData,
  cityName,
  initialIsMobile, 
  initialHideHeader 
}: HeaderClientWrapperProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const isMobile = useIsMobile();
  const hideHeader = shouldHideHeader(pathname);

  const [isClient, setIsClient] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [hasClosedIntentModal, setHasClosedIntentModal] = useState(false);
  const [mmvDetails, setMmvDetails] = useState({});
  const [buyingIntent, setBuyingIntent] = useState<string | null>(null);
  const [loginType, setLoginType] = useState<string | null>(null);
  const [pageType, setPageType] = useState<PageType | null>(null);
  
  const hasCaptureIntentParam = searchParams?.get('captureIntent') === 'true';

  useEffect(() => {
    setIsClient(true);
    if (typeof window !== 'undefined') {
      const mmvData = sessionStorage.getItem('mmvDetails');
      const buyingIntentData = sessionStorage.getItem('buying_intent');
      const pageType = sessionStorage.getItem('pageType');
      const loginType = sessionStorage.getItem('loginType');
      
      setMmvDetails(mmvData ? JSON.parse(mmvData) : {});
      setBuyingIntent(buyingIntentData);
      setPageType(pageType ? JSON.parse(pageType) : null);
      setLoginType(loginType || null);
    }
  }, []);

  const alwaysHideHeaderPaths = ['/campaign-cars/', '/car-drop/', '/car-drop/terms-and-conditions/','/remind-you-later/', '/car-drop/winner/terms-and-conditions/', '/car-drop/winner/'];
  const shouldAlwaysHide = alwaysHideHeaderPaths.some(path => pathname?.includes(path));

  const shouldHeaderHide = shouldAlwaysHide || (isClient ? (isMobile && hideHeader) : (initialIsMobile && initialHideHeader));

  const shouldShowModal = Boolean(
    isAuthenticated && 
    hasCaptureIntentParam && 
    buyingIntent !== 'false' &&
    buyingIntentData &&
    !hasClosedIntentModal
  );
  
  useEffect(() => {
    if (shouldShowModal && !isIntentModalOpen) {
      setIsIntentModalOpen(true);
    }
  }, [shouldShowModal, isIntentModalOpen]);

  const handleModalClose = () => {
    setIsIntentModalOpen(false);
    setHasClosedIntentModal(true);
    const currentParams = searchParams ? searchParams.toString() : "";
    const newSearchParams = new URLSearchParams(currentParams);
    newSearchParams.delete("captureIntent");
    const newUrl = newSearchParams.toString()
      ? `${pathname}?${newSearchParams.toString()}`
      : pathname;
    router.replace(newUrl || "");
  };

  if (shouldHeaderHide) {
    return (
      <>
        {shouldShowModal && (
          <GlobalIntentModal
            isOpen={isIntentModalOpen}
            onClose={handleModalClose}
            buyingIntent={buyingIntentData}
            cityName={cityName}
            mmvDetails={mmvDetails}
            pageType={pageType}
            loginType={loginType}
          />
        )}
      </>
    );
  }

  return (
    <header className={styles.headerWrapper}>
      {children}

      {/* Login Modal */}
      <LoginModal
        isLoginModalOpen={isLoginModalOpen}
        setIsLoginModalOpen={setIsLoginModalOpen}
      />

      {/* Global Intent Modal */}
      {shouldShowModal && (
        <GlobalIntentModal
          isOpen={isIntentModalOpen}
          onClose={handleModalClose}
          buyingIntent={buyingIntentData}
          cityName={cityName}
          mmvDetails={mmvDetails}
          pageType={pageType}
          loginType={loginType}
        />
      )}
    </header>
  );
}

export default HeaderClientWrapper;
